import React, { useState, useRef } from "react";

import InputField from "../../common/InputField";
import { Styles } from "./postJobForm";
import { colors } from "../../../utils/Colors";
import { PostJobSchema } from "../../../utils/PostJobSchema";
import { postJob } from "../../../store/thunks";
import Button from "../../common/Button";
import Error from "../../common/Error";
import PicturePicker from "../../common/PicturePicker";

import Select from "react-select";
import { useFormik } from "formik";
import { FaDollarSign } from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppleAutoComplete from "../../../components/common/AppleAutoComplete";
import { enhanceJobWithAI, getCurrentLocation, generateJobImage, base64ToFile } from "../../../services/aiService";

const work = [
  { value: "on-site", label: "On-Site" },
  { value: "online", label: "Online" },
];

const handleWork = (workType) => {
  if (workType === "on-site") {
    return { value: "on-site", label: "On-Site" };
  }
  if (workType === "online") {
    return { value: "online", label: "Online" };
  }
};
const handleType = (type) => {
  if (type === "Part Time") {
    return { value: "Part Time", label: "Part Time" };
  }
  if (type === "Full Time") {
    return { value: "Full Time", label: "Full Time" };
  }
  if (type === "Contract") {
    return { value: "Contract", label: "Contract" };
  }
};
const handleSubType = (type) => {
  if (type === "Hourly") {
    return { value: "Hourly", label: "Hourly" };
  }
  if (type === "Weekly") {
    return { value: "Weekly", label: "Weekly" };
  }
  if (type === "Monthly") {
    return { value: "Monthly", label: "Monthly" };
  }

  if (type === "Yearly") {
    return { value: "Yearly", label: "Yearly" };
  }
};

const delivery = [];

const makeDay = (num) => {
  let day = "Day";
  if (num > 1) day = "Days";
  return { value: num, label: `${num} ${day}` };
};
for (let i = 1; i <= 30; i++) {
  delivery.push(makeDay(i));
}
const jobTypes = [
  { label: "Part Time", value: "Part Time" },
  { label: "Full Time", value: "Full Time" },
  { label: "Contract", value: "Contract" },
];
const subType = [
  { label: "Hourly", value: "Hourly" },
  { label: "Weekly", value: "Weekly" },
  { label: "Monthly", value: "Monthly" },
  { label: "Yearly", value: "Yearly" },
];

const PostJobForm = ({ onSubmit, jobValues, addressLabel = "" }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [subCategoryList, setsubCategoryList] = useState([]);
  const [coordinates, setCoordinates] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiEnhanced, setAiEnhanced] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const autoCompleteRef = useRef();
  const { categories } = useSelector((state) => state.categories);
  const { loading } = useSelector((state) => state.jobs);
  const handleSelectChange = (selected) => {
    setFieldValue("type", selected.value);
  };

  const handleImageUpload = (file, index) => {
    const images = [...values.attachments];
    for (let i = 0; i <= 2; i++) {
      if (i === index) {
        images[i] = file;
      }
    }
    setFieldValue(
      "attachments",
      images.filter((image) => image)
    );
  };

  const onSelectPlace = (location) => {
    const {
      coordinate: { latitude, longitude },
      description,
    } = location.value;
    setCoordinates({
      latitude,
      longitude,
    });
    setFieldValue("location", {
      latitude,
      longitude,
      address: description,
    });
  };

  const handleAIEnhance = async () => {
    if (!values.description.trim() || values.description.length < 10) {
      setAiError("Please enter at least 10 characters in the job description");
      return;
    }

    if (values.description.length > 500) {
      setAiError("Job description cannot exceed 500 characters");
      return;
    }

    setAiLoading(true);
    setAiError(null);

    try {
      const originalDescription = values.description;

      // Call backend — usage check + increment + OpenAI all happen server-side
      const enhancedJob = await enhanceJobWithAI(originalDescription, (streamText) => {
        // Stream the description field live while generating
        try {
          const descMatch = streamText.match(/"description"\s*:\s*"([^"]*)"/);
          if (descMatch && descMatch[1]) {
            setFieldValue("description", descMatch[1]);
          }
        } catch (e) {
          // ignore partial JSON parse errors
        }
      });

      // ✅ Populate ALL fields immediately — stops the spinner right away
      if (enhancedJob.description) setFieldValue("description", enhancedJob.description);
      if (enhancedJob.type)        setFieldValue("type", enhancedJob.type);
      if (enhancedJob.subType)     setFieldValue("subType", enhancedJob.subType);
      if (enhancedJob.workType)    setFieldValue("workType", enhancedJob.workType);
      if (enhancedJob.deadline)    setFieldValue("deadline", enhancedJob.deadline);
      if (enhancedJob.budgetMin)   setFieldValue("budgetMin", enhancedJob.budgetMin);
      if (enhancedJob.budgetMax)   setFieldValue("budgetMax", enhancedJob.budgetMax);

      setAiLoading(false);
      setAiEnhanced(true);

      // 🌍 Location & 🖼️ Image run in the background — don't block the form
      getCurrentLocation()
        .then((locationData) => {
          setFieldValue("location", locationData);
          setCoordinates({ latitude: locationData.latitude, longitude: locationData.longitude });
          if (autoCompleteRef.current) {
            autoCompleteRef.current.value = locationData.address;
          }
        })
        .catch((err) => console.error("Location error:", err));

      setImageLoading(true);
      generateJobImage(enhancedJob.description || originalDescription)
        .then((base64DataUrl) => base64ToFile(base64DataUrl))
        .then((imageFile) => {
          const images = [...values.attachments];
          images[0] = imageFile;
          setFieldValue("attachments", images.filter(Boolean));
        })
        .catch((err) => console.error("Image generation error:", err))
        .finally(() => setImageLoading(false));

    } catch (error) {
      console.error("AI Enhancement Error:", error);
      setAiError(
        error.response?.data?.error ||
        error.message ||
        "Failed to enhance job with AI. Please try again."
      );
      setAiLoading(false);
    }
  };

  const handleDescriptionBlur = (e) => {
    handleBlur(e);
    handleAIEnhance();
  };

  const initialValues = {
    type: jobValues?.type || "",
    subType: jobValues?.subType || "",
    workType: jobValues?.workType || "",
    deadline: jobValues?.deadline || "",
    budgetMax: jobValues?.budgetMax || "",
    budgetMin: jobValues?.budgetMin || "",
    description: jobValues?.description || "",
    attachments: jobValues?.attachments || [],
    location: jobValues?.location || {},
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema: PostJobSchema,
    onSubmit: (values, action) => {
      console.log(values, "valuesvaluesvalues");
      if (jobValues) {
        onSubmit(values);
      } else {
        values["jobType"] = "employer";
        dispatch(postJob({ job: values, navigate }));
        action.resetForm();
      }
    },
  });

  return (
    <Styles colors={colors}>
      <form className="service-looking" onSubmit={handleSubmit}>
        <label className="service-looking-question">
          Describe Your Job
        </label>
        <div className="text-field">
          <textarea
            placeholder="Example: Need lawn mowing tomorrow"
            name="description"
            id="description"
            value={values.description}
            onChange={(e) => {
              handleChange(e);
              setAiEnhanced(false); // Reset when user types
            }}
            onBlur={handleBlur}
            autoComplete="off"
            disabled={aiLoading}
            maxLength={500}
          ></textarea>
          <p>{values.description.length}/500</p>
        </div>
        <p className="text-muted" style={{ fontSize: "14px", marginTop: "8px", marginBottom: "0" }}>
          💡 Describe your job in one sentence and AI will create the full post
        </p>
        {errors.description && touched.description && (
          <Error text={errors.description} />
        )}
        
        {/* AI Enhance Button */}
        <div className="mt-3 mb-3">
          <Button
            type="button"
            onClick={handleAIEnhance}
            disabled={aiLoading || !values.description.trim() || values.description.length < 10}
            className="btn btn-primary"
            style={{
              backgroundColor: aiLoading ? "#6c757d" : colors.primary,
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: aiLoading || !values.description.trim() || values.description.length < 10 ? "not-allowed" : "pointer",
              opacity: aiLoading || !values.description.trim() || values.description.length < 10 ? 0.6 : 1,
              color: "white",
              fontWeight: "500",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {aiLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                Generating Job Post...
              </>
            ) : (
              <>
                <span style={{ fontSize: "20px" }}>✨</span>
                Generate Job Post with AI
              </>
            )}
          </Button>
        </div>
        
        {imageLoading && (
          <div className="mt-2 mb-3">
            <div className="alert alert-info" role="alert" style={{ fontSize: "14px" }}>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              🖼️ Generating AI image in the background...
            </div>
          </div>
        )}
        
        {aiError && (
          <div className="mt-2 mb-3">
            <div className="alert alert-warning alert-dismissible fade show" role="alert" style={{ fontSize: "14px" }}>
              {aiError}
              <button
                type="button"
                className="btn-close"
                onClick={() => setAiError(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        )}
        
        {aiEnhanced && !aiError && (
          <div className="mt-2 mb-3">
            <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ fontSize: "14px" }}>
              ✅ Job details have been enhanced with AI and pre-filled!
              <button
                type="button"
                className="btn-close"
                onClick={() => setAiEnhanced(false)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        )}

        <hr />
        <div className="d-flex flex-column gap-4">
          <div className="d-flex flex-wrap gap-4">
            <div style={{ flex: 1 }}>
              <label htmlFor="jobType" className="select-label">
                Job type
              </label>
              <Select
                id="jobType"
                name="jobType"
                className="select"
                placeholder="Select Job Type"
                options={jobTypes}
                getOptionLabel={(option) => {
                  return <div className="text-capitalize">{option.label}</div>;
                }}
                getOptionValue={(option) => option}
                onChange={handleSelectChange}
                value={handleType(values.type)}
              />
              {errors.type && touched.type && <Error text={errors.type} />}
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="subType" className="select-label">
                Choose Sub Type
              </label>
              <Select
                id="subType"
                name="subType"
                className="select"
                placeholder="Select Sub Type..."
                options={subType}
                getOptionLabel={(option) => {
                  return <div className="text-capitalize">{option.value}</div>;
                }}
                getOptionValue={(option) => option}
                onChange={(value) => {
                  setFieldValue("subType", value.value);
                }}
                value={handleSubType(values.subType)}
              />
              {errors.subType && touched.subType && (
                <Error text={errors.subType} />
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-4">
            <div style={{ flex: 1 }}>
              <label htmlFor="workType" className="select-label">
                Choose workType
              </label>
              <Select
                id="workType"
                name="workType"
                className="select"
                onChange={(selectedOption) => {
                  setFieldValue("workType", selectedOption.value);
                }}
                options={work}
                getOptionLabel={(option) => {
                  return <div>{option.label}</div>;
                }}
                getOptionValue={(option) => option}
                value={handleWork(values.workType)}
              />
              {errors.workType && touched.workType && (
                <Error text={errors.workType} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="duration" className="select-label">
                Delivery Time
              </label>
              <Select
                id="deadline"
                name="deadline"
                className="select"
                onChange={(selectedOption) =>
                  setFieldValue("deadline", selectedOption.value)
                }
                options={delivery}
                getOptionLabel={(option) => {
                  return <div>{option.label}</div>;
                }}
                getOptionValue={(option) => option}
                value={makeDay(values.deadline)}
              />
              {errors.deadline && touched.deadline && (
                <Error text={errors.deadline} />
              )}
            </div>
          </div>
          <div>
            <label className="select-label">Location</label>
            <AppleAutoComplete
              style={{ width: "100%", borderRadius: 4 }}
              onSelect={onSelectPlace}
              coordinate={coordinates}
              placeholder={values.location?.address || "Search location..."}
            />
            {/* <p className="text-sm text-muted mt-1">
              {"Please enter a valid US address."}
            </p> */}
            {errors.location && touched.location && (
              <Error text={errors.location} />
            )}
          </div>
        </div>
        <div className="budget">
          <label>Rate Range</label>
          <div className="budget-box">
            <div>
              <div className="budget-min-max">
                <p>
                  Min - <FaDollarSign />
                </p>
                <InputField
                  className="budget-input"
                  type="number"
                  name="budgetMin"
                  id="budgetMin"
                  value={values.budgetMin}
                  onChange={handleChange}
                  autoComplete="off"
                  onBlur={handleBlur}
                />
              </div>
              {errors.budgetMin && touched.budgetMin && (
                <Error text={errors.budgetMin} />
              )}
            </div>

            <div>
              <div className="budget-min-max">
                <p>
                  Max - <FaDollarSign />
                </p>
                <InputField
                  className="budget-input"
                  type="number"
                  name="budgetMax"
                  id="budgetMax"
                  value={values.budgetMax}
                  onChange={handleChange}
                  autoComplete="off"
                  onBlur={handleBlur}
                />
              </div>
              {errors.budgetMax && touched.budgetMax && (
                <Error text={errors.budgetMax} />
              )}
            </div>
          </div>
        </div>
        <hr />
        <div className="attachment-box">
          <label>Add Attachment</label>

          <div className="add">
            {[1, 2, 3].map((picker, index) => {
              let image = values.attachments[index];
              if (image?.imgUrl) {
                image = values.attachments[index].imgUrl;
              }
              return (
                <PicturePicker
                  key={index}
                  index={index}
                  setImage={handleImageUpload}
                  image={image}
                />
              );
            })}
          </div>
          {errors.attachments && touched.attachments && (
            <Error text={errors.attachments} />
          )}
        </div>
        <div className="submit-job">
          <Button
            children={
              loading ? (
                <div
                  className="spinner-border spinner-border-sm and spinner-grow-sm mb-0"
                  role="status"
                >
                  <span className="sr-only"></span>
                </div>
              ) : (
                "Submit Job"
              )
            }
            className="submit-job-btn"
            type="submit"
            disabled={loading}
          />
        </div>
      </form>
    </Styles>
  );
};

export default PostJobForm;
